import { useHospitals } from './useHospitals';
import { Hospital } from '../types/api';
import { useToast } from '../components/ui/toast';
import { useEffect, useCallback, useMemo } from 'react';

export const useHospital = () => {
  const {
    hospitals,
    selectedHospital,
    loading,
    error,
    switchingTenant,
    userVerificationLoading,
    userVerificationError,
    selectHospital,
    refreshHospitals,
    hasMultipleHospitals,
  } = useHospitals();

  const { addToast } = useToast();

  // Show toast notification when verification error occurs
  useEffect(() => {
    if (userVerificationError) {
      addToast({
        message: userVerificationError,
        duration: 5000,
      });
    }
  }, [userVerificationError, addToast]);

  const getSelectedHospitalData = useCallback((): Hospital | undefined => {
    return selectedHospital || undefined;
  }, [selectedHospital]);

  const updateSelectedHospital = useCallback(
    async (hospitalName: string) => {
      const hospital = hospitals.find((h) => h.hospitalName === hospitalName);
      if (hospital) {
        await selectHospital(hospital);
      }
    },
    [hospitals, selectHospital],
  );

  // Memoize the return value to prevent unnecessary re-renders
  const result = useMemo(
    () => ({
      selectedHospital: selectedHospital?.hospitalName || '',
      hospitals: hospitals?.map((h) => ({
        name: h.hospitalName,
        code: h.hospitalCode,
        tenantId: h.tenantId,
      })),
      getSelectedHospitalData,
      updateSelectedHospital,
      fetchHospitals: refreshHospitals,
      loading,
      error,
      switchingTenant,
      userVerificationLoading,
      userVerificationError,
      hasMultipleHospitals,
    }),
    [
      selectedHospital,
      hospitals,
      getSelectedHospitalData,
      updateSelectedHospital,
      refreshHospitals,
      loading,
      error,
      switchingTenant,
      userVerificationLoading,
      userVerificationError,
      hasMultipleHospitals,
    ],
  );

  return result;
};
